// Processing-attach RPC client wrappers for the authenticated app.
//
// These call the transactional SECDEF RPCs added in migration
// 096_processing_attach_activity_rpcs.sql. The public WeighInsWebform anon
// path can still use the legacy client helpers through the shared modals; the
// authenticated weigh-in session record page opts into these RPCs.

import type {RpcEnvelope, SupabaseClient} from './supabaseTypes.js';

// `data` may legitimately be absent (a null jsonb result), which is why the
// first branch exists at all — so the parameter admits that rather than
// promising an envelope the caller has not yet proved it has.
function requireOk(data: RpcEnvelope | null | undefined, label: string): RpcEnvelope {
  if (!data) throw new Error(label + ': no result');
  if (data.ok !== true) {
    const reason = data.reason || data.error || 'not_ok';
    throw new Error(label + ': ' + reason);
  }
  return data;
}

// p_entry_ids is text[] in migration 096; the date arguments are `date` and are
// passed as ISO day strings. Every field is optional because the options object
// defaults to `{}` and the RPC does its own required-argument validation.
export type AttachCattleOptions = {
  sessionId?: string;
  entryIds?: string[];
  targetBatchId?: string | null;
  batchName?: string | null;
  processingDate?: string | null;
  teamMember?: string | null;
};

export type AttachSheepOptions = {
  sessionId?: string;
  entryIds?: string[];
  targetBatchId?: string | null;
  batchName?: string | null;
  plannedDate?: string | null;
  teamMember?: string | null;
};

export async function attachCattleToProcessingBatch(
  sb: SupabaseClient,
  {
    sessionId,
    entryIds,
    targetBatchId = null,
    batchName = null,
    processingDate = null,
    teamMember = null,
  }: AttachCattleOptions = {},
): Promise<RpcEnvelope> {
  const {data, error} = await sb.rpc('attach_cattle_to_processing_batch', {
    p_session_id: sessionId,
    p_entry_ids: entryIds || [],
    p_target_batch_id: targetBatchId || null,
    p_batch_name: batchName || null,
    p_processing_date: processingDate || null,
    p_team_member: teamMember || null,
  });
  if (error) throw new Error('attachCattleToProcessingBatch: ' + (error.message || String(error)));
  return requireOk(data, 'attachCattleToProcessingBatch');
}

export async function attachSheepToProcessingBatch(
  sb: SupabaseClient,
  {
    sessionId,
    entryIds,
    targetBatchId = null,
    batchName = null,
    plannedDate = null,
    teamMember = null,
  }: AttachSheepOptions = {},
): Promise<RpcEnvelope> {
  const {data, error} = await sb.rpc('attach_sheep_to_processing_batch', {
    p_session_id: sessionId,
    p_entry_ids: entryIds || [],
    p_target_batch_id: targetBatchId || null,
    p_batch_name: batchName || null,
    p_planned_date: plannedDate || null,
    p_team_member: teamMember || null,
  });
  if (error) throw new Error('attachSheepToProcessingBatch: ' + (error.message || String(error)));
  return requireOk(data, 'attachSheepToProcessingBatch');
}
